import { useState } from 'react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { WarrantyStatus } from './WarrantyStatus';
import { ApexReference } from './ApexReference';

export default function UserJobUpdateModal({ appointment, onClose, onSuccess }) {
  const [status, setStatus] = useState(appointment.status);
  const [newNote, setNewNote] = useState('');
  const [workCompleted, setWorkCompleted] = useState('');
  const [completionNotes, setCompletionNotes] = useState(appointment.completion_notes || '');
  const [startTime, setStartTime] = useState(appointment.start_time);
  const [endTime, setEndTime] = useState(appointment.end_time);
  const [workType, setWorkType] = useState(appointment.work_type || 'onsite');
  const [actualHours, setActualHours] = useState(appointment.actual_hours || '');
  const [useSimpleHours, setUseSimpleHours] = useState(true); // Toggle between times or hours
  const [loading, setLoading] = useState(false);

  const statusOptions = [
    'Open',
    'In Progress',
    'Waiting for Customer Update',
    'Waiting for Parts',
    'Completed',
    'Closed'
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const updateData = {
        status: status !== appointment.status ? status : undefined,
        notes: newNote.trim() ? newNote : undefined,
        work_completed: workCompleted.trim() ? workCompleted : undefined,
        start_time: startTime !== appointment.start_time ? startTime : undefined,
        end_time: endTime !== appointment.end_time ? endTime : undefined,
        work_type: workType !== appointment.work_type ? workType : undefined,
        completion_notes: completionNotes !== appointment.completion_notes ? completionNotes : undefined,
        actual_hours: actualHours !== appointment.actual_hours ? parseFloat(actualHours) || undefined : undefined
      };

      // Remove undefined fields
      Object.keys(updateData).forEach(key => {
        if (updateData[key] === undefined) {
          delete updateData[key];
        }
      });

      if (Object.keys(updateData).length === 0) {
        toast.error('No changes to update');
        setLoading(false);
        return;
      }

      await axios.put(
        `${API}/appointments/${appointment.id}/user-update`,
        updateData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Job updated successfully!');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update job');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="update-job-modal" className="fixed inset-0 bg-black bg-opacity-75 flex items-start justify-center p-4 z-50 overflow-y-auto job-modal">
      <div className="retro-panel max-w-2xl w-full my-8 max-h-[90vh] overflow-y-auto">
        <div className="mb-4"><ApexReference job={appointment} scope="update" /></div>
        <h2 className="text-2xl md:text-3xl font-bold mb-4 md:mb-6 glow-text" style={{ color: '#FF6B00' }}>
          [UPDATE JOB]
        </h2>

        {/* Job Info */}
        <div className="mb-4 md:mb-6 p-3 md:p-4 border-2" style={{ borderColor: '#FF6B00' }}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm" style={{ color: '#ffffff' }}>
            <div><strong style={{ color: '#FF6B00' }}>Customer:</strong> {appointment.customer_name}</div>
            <div><strong style={{ color: '#FF6B00' }}>Job Type:</strong> {appointment.job_type}</div>
            <div><strong style={{ color: '#FF6B00' }}>Date:</strong> {appointment.appointment_date}</div>
            <div><strong style={{ color: '#FF6B00' }}>Time:</strong> {appointment.start_time} - {appointment.end_time}</div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
          <WarrantyStatus appointment={appointment} scope="job-update" />
          {/* Status */}
          <div>
            <label className="block mb-2 text-lg font-bold" style={{ color: '#FF6B00' }}>
              [STATUS]:
            </label>
            <select
              value={status}
              data-testid="update-job-status"
              onChange={(e) => setStatus(e.target.value)}
              className="retro-input w-full"
              required
            >
              {statusOptions.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
            {status === 'Closed' && (
              <div className="mt-2 p-2 border" style={{ borderColor: '#00FF00', backgroundColor: '#001a00' }}>
                <div className="text-sm" style={{ color: '#00FF00' }}>
                  ✓ Closing this job will automatically create a work entry for time tracking!
                </div>
              </div>
            )}
          </div>

          {/* Time Entry Toggle */}
          <div className="border-2 p-4" style={{ borderColor: '#FF6B00' }}>
            <div className="flex gap-4 mb-4">
              <button
                type="button"
                onClick={() => setUseSimpleHours(true)}
                data-testid="update-job-hours-mode"
                className={`retro-btn flex-1 ${useSimpleHours ? 'retro-btn-green' : ''}`}
                style={{ 
                  backgroundColor: useSimpleHours ? '#00FF00' : undefined,
                  color: useSimpleHours ? '#000' : undefined
                }}
              >
                [ENTER TOTAL HOURS]
              </button>
              <button
                type="button"
                onClick={() => setUseSimpleHours(false)}
                data-testid="update-job-time-mode"
                className={`retro-btn flex-1 ${!useSimpleHours ? 'retro-btn-green' : ''}`}
                style={{ 
                  backgroundColor: !useSimpleHours ? '#00FF00' : undefined,
                  color: !useSimpleHours ? '#000' : undefined
                }}
              >
                [ENTER START/END TIMES]
              </button>
            </div>

            {useSimpleHours ? (
              // Simple hours input
              <div>
                <label className="block mb-2 text-lg font-bold" style={{ color: '#FF6B00' }}>
                  [TOTAL HOURS WORKED]:
                </label>
                <input
                  type="number"
                  value={actualHours}
                  data-testid="update-job-hours"
                  onChange={(e) => setActualHours(e.target.value)}
                  step="0.25"
                  min="0"
                  className="retro-input w-full"
                  placeholder="e.g., 3.5"
                  style={{
                    backgroundColor: '#000',
                    borderColor: '#FF6B00',
                    color: '#FF6B00'
                  }}
                />
                {appointment.estimated_hours && (
                  <div className="mt-2 text-sm" style={{ color: '#FFD60A' }}>
                    Estimated: {appointment.estimated_hours} hours
                  </div>
                )}
              </div>
            ) : (
              // Start/End times
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block mb-2 text-lg font-bold" style={{ color: '#FF6B00' }}>
                    [START TIME]:
                  </label>
                  <input
                    type="time"
                    value={startTime}
                    data-testid="update-job-start-time"
                    onChange={(e) => setStartTime(e.target.value)}
                    className="retro-input w-full"
                  />
                </div>
                <div>
                  <label className="block mb-2 text-lg font-bold" style={{ color: '#FF6B00' }}>
                    [FINISH TIME]:
                  </label>
                  <input
                    type="time"
                    value={endTime}
                    data-testid="update-job-end-time"
                    onChange={(e) => setEndTime(e.target.value)}
                    className="retro-input w-full"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Work Type */}
          <div>
            <label className="block mb-2 text-lg font-bold" style={{ color: '#00D4FF' }}>
              [WORK TYPE]:
            </label>
            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => setWorkType('onsite')}
                data-testid="update-job-onsite"
                className={`retro-btn flex-1 ${workType === 'onsite' ? 'retro-btn-green' : ''}`}
                style={{ 
                  backgroundColor: workType === 'onsite' ? '#00FF00' : undefined,
                  color: workType === 'onsite' ? '#000' : undefined
                }}
              >
                [ONSITE]
              </button>
              <button
                type="button"
                onClick={() => setWorkType('remote')}
                data-testid="update-job-remote"
                className={`retro-btn flex-1 ${workType === 'remote' ? 'retro-btn-yellow' : ''}`}
                style={{ 
                  backgroundColor: workType === 'remote' ? '#FFD60A' : undefined,
                  color: workType === 'remote' ? '#000' : undefined
                }}
              >
                [REMOTE]
              </button>
            </div>
          </div>

          {/* Work Completed Section - For Technicians */}
          <div>
            <label className="block mb-2 text-lg font-bold" style={{ color: '#00FF00' }}>
              [WORK COMPLETED - TECHNICIAN UPDATE]:
            </label>
            <textarea
              value={workCompleted}
              data-testid="update-job-work-completed"
              onChange={(e) => setWorkCompleted(e.target.value)}
              className="retro-input w-full"
              rows="4"
              placeholder="Document what work was completed (remote or onsite)...&#10;Example:&#10;- Installed new router&#10;- Configured network settings&#10;- Tested connectivity&#10;- Customer approved"
              style={{ borderColor: '#00FF00' }}
            />
            <div className="text-xs mt-1" style={{ color: '#00FF00' }}>
              ✓ Use this section to document all work performed on this job
            </div>
          </div>

          {/* Add General Note */}
          <div>
            <label className="block mb-2 text-lg font-bold" style={{ color: '#FF6B00' }}>
              [GENERAL NOTES]:
            </label>
            <textarea
              value={newNote}
              data-testid="update-job-notes"
              onChange={(e) => setNewNote(e.target.value)}
              className="retro-input w-full"
              rows="2"
              placeholder="Add any additional notes or comments..."
            />
          </div>

          {/* Completion Notes */}
          <div>
            <label className="block mb-2 text-lg font-bold" style={{ color: '#FF6B00' }}>
              [FINAL COMPLETION SUMMARY]:
            </label>
            <textarea
              value={completionNotes}
              data-testid="update-job-completion-notes"
              onChange={(e) => setCompletionNotes(e.target.value)}
              className="retro-input w-full"
              rows="2"
              placeholder="Final summary when closing the job..."
            />
            <div className="text-xs mt-1" style={{ color: '#ffffff' }}>
              * Use this field for final completion summary
            </div>
          </div>

          {/* Notes History */}
          {appointment.notes_history && appointment.notes_history.length > 0 && (
            <div>
              <label className="block mb-2 text-lg font-bold" style={{ color: '#FF6B00' }}>
                [UPDATE HISTORY]:
              </label>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {appointment.notes_history.map((note, index) => {
                  const isWorkUpdate = note.note_type === 'work_completed';
                  const isSystemNote = note.username === 'SYSTEM';
                  const borderColor = isWorkUpdate ? '#00FF00' : isSystemNote ? '#00D4FF' : '#FF6B00';
                  const bgColor = isWorkUpdate ? '#001a00' : isSystemNote ? '#001a1a' : '#0a0a0a';
                  
                  return (
                    <div key={index} className="p-3 border" style={{ borderColor, backgroundColor: bgColor }}>
                      <div className="flex justify-between text-xs mb-1" style={{ color: borderColor }}>
                        <span className="font-bold">
                          {isWorkUpdate && '✓ WORK COMPLETED - '}
                          {note.username}
                        </span>
                        <span>{new Date(note.timestamp).toLocaleString()}</span>
                      </div>
                      <div style={{ color: '#ffffff', whiteSpace: 'pre-wrap' }}>{note.note}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Buttons */}
          <div className="flex gap-4">
            <button
              type="submit"
              data-testid="update-job-submit"
              className="retro-btn retro-btn-green flex-1"
              disabled={loading}
            >
              {loading ? '[UPDATING...]' : '[UPDATE JOB]'}
            </button>
            <button
              type="button"
              onClick={onClose}
              data-testid="update-job-cancel"
              className="retro-btn retro-btn-magenta flex-1"
              disabled={loading}
            >
              [CANCEL]
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
